<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="contact-modal-overlay"
      role="presentation"
      @click.self="requestClose"
    >
      <section
        ref="dialog"
        class="contact-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        @click.stop
      >
        <button ref="closeButton" class="contact-modal__close" type="button" :aria-label="t('contact.closeAria')" @click="requestClose">
          <img src="/static/images/contact-modal/close.svg" alt="" width="24" height="24" aria-hidden="true">
        </button>

        <aside class="contact-modal__details">
          <div>
            <h2 id="contact-modal-title">{{ t('contact.title') }}</h2>
            <p class="contact-modal__intro">{{ t('contact.intro') }}</p>
          </div>

          <div class="contact-modal__office">
            <h3>{{ t('contact.headOffice') }}</h3>
            <p v-if="currentLanguage === 'ZH' && contact.phone">
              <img src="/static/images/contact-modal/phone.svg" alt="" width="18" height="18" aria-hidden="true">
              <span>{{ t('contact.phone') }}{{ contact.phone }}</span>
            </p>
            <p v-if="currentLanguage === 'EN' && contact.email">
              <img src="/static/images/contact-modal/email.svg" alt="" width="18" height="13" aria-hidden="true">
              <a :href="`mailto:${contact.email}`">{{ t('contact.email') }}{{ contact.email }}</a>
            </p>
            <p v-if="currentLanguage === 'ZH' && contact.wechatNo">
              <img src="/static/images/footer/wechat.svg" alt="" width="18" height="18" aria-hidden="true">
              <span>微信：{{ contact.wechatNo }}</span>
            </p>
            <button
              v-if="currentLanguage === 'ZH' && contact.wechatQrCodeUrl"
              class="contact-modal__office-link"
              type="button"
              @click="wechatQrCodeVisible = true"
            >
              <img src="/static/images/footer/wechat.svg" alt="" width="18" height="18" aria-hidden="true">
              微信二维码
            </button>
            <a
              v-if="currentLanguage === 'EN' && whatsAppUrl"
              class="contact-modal__office-link"
              :href="whatsAppUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/static/images/contact-modal/whatsapp.svg" alt="" width="18" height="18" aria-hidden="true">
              WhatsApp
            </a>
          </div>
        </aside>

        <form class="contact-modal__form" novalidate @submit="submitForm">
          <div class="contact-modal__fields">
            <label class="contact-modal__field" :class="{ 'has-error': errors.name }">
              <span class="sr-only">{{ t('contact.namePlaceholder') }}</span>
              <input ref="nameInput" v-model.trim="fields.name" name="name" type="text" autocomplete="name" :placeholder="t('contact.namePlaceholder')" @input="clearError('name')">
              <span v-if="errors.name" class="contact-modal__error">{{ errors.name }}</span>
            </label>

            <label class="contact-modal__field" :class="{ 'has-error': errors.email }">
              <span class="sr-only">{{ t('contact.emailPlaceholder') }}</span>
              <input ref="emailInput" v-model.trim="fields.email" name="email" type="email" autocomplete="email" :placeholder="t('contact.emailPlaceholder')" @input="clearError('email')">
              <span v-if="errors.email" class="contact-modal__error">{{ errors.email }}</span>
            </label>

            <div class="contact-modal__field contact-modal__field--wide" :class="{ 'has-error': errors.whatsapp }">
              <span class="sr-only">{{ t('contact.phonePlaceholder') }}</span>
              <CountryPhoneInput ref="whatsappInput" v-model="fields.whatsapp" v-model:country="phoneCountry" :label="t('contact.phonePlaceholder')" :country-label="currentLanguage === 'ZH' ? '国家/地区区号' : 'Country / region calling code'" @update:modelValue="clearError('whatsapp')" />
              <span v-if="errors.whatsapp" class="contact-modal__error">{{ errors.whatsapp }}</span>
            </div>

            <label class="contact-modal__field contact-modal__field--wide" :class="{ 'has-error': errors.message }">
              <span class="sr-only">{{ t('contact.messagePlaceholder') }}</span>
              <textarea ref="messageInput" v-model.trim="fields.message" name="message" rows="2" :placeholder="t('contact.messagePlaceholder')" @input="clearError('message')" />
              <span v-if="errors.message" class="contact-modal__error">{{ errors.message }}</span>
            </label>
          </div>

          <label class="contact-modal__privacy" :class="{ 'has-error': errors.privacyPolicyAccepted }">
            <input v-model="fields.privacyPolicyAccepted" name="privacyPolicyAccepted" type="checkbox" value="true" @change="clearError('privacyPolicyAccepted')">
            <span>{{ t('contact.privacyPrefix') }}<a :href="privacyPolicyHref" target="_blank" rel="noopener noreferrer">{{ t('contact.privacyLink') }}</a>{{ t('contact.privacySuffix') }}</span>
          </label>
          <p v-if="errors.privacyPolicyAccepted" class="contact-modal__privacy-error" role="alert">{{ errors.privacyPolicyAccepted }}</p>

          <p v-if="submitStatus.message" class="contact-modal__status" :class="`is-${submitStatus.type}`" role="status" aria-live="polite">{{ submitStatus.message }}</p>

          <button class="contact-modal__submit" type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? t('contact.submitting') : t('contact.submit') }}
          </button>
        </form>

        <div v-if="wechatQrCodeVisible" class="contact-modal__qr-overlay" @click.self="wechatQrCodeVisible = false">
          <section class="contact-modal__qr-dialog" role="dialog" aria-modal="true" aria-label="微信二维码">
            <button class="contact-modal__qr-close" type="button" aria-label="关闭微信二维码" @click="wechatQrCodeVisible = false">×</button>
            <img :src="contact.wechatQrCodeUrl" alt="微信二维码">
          </section>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script>
import { contactWayAPI } from '@/utils/api'
import CountryPhoneInput from './CountryPhoneInput.vue'

const { createWhatsAppUrl } = require('@/utils/contact-modal.cjs')

const { createContactRequest, validateContactFields, readContactResponse } = require('@/utils/contact-form.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

function createEmptyFields() {
  return {
    name: '',
    email: '',
    whatsapp: '',
    message: '',
    hiddenField: '',
    countryField: '',
    countryCodeField: '',
    countryNameField: '',
    countryManualSelected: 'n',
    dialCodeField: '',
    fromPage: 'contact',
    privacyPolicyAccepted: false
  }
}

export default {
  name: 'ContactModal',
  components: { CountryPhoneInput },
  mixins: [siteLanguageMixin],
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      fields: createEmptyFields(),
      phoneCountry: 'us',
      contact: { phone: '', email: '', whatsapp: '', wechatNo: '', wechatQrCodeUrl: '' },
      wechatQrCodeVisible: false,
      errors: {},
      isSubmitting: false,
      submitStatus: { type: '', message: '' },
      contactRequestGuard: createLatestSiteRequestGuard(),
      previousBodyOverflow: '',
      previousBodyPaddingRight: '',
      previousRootOverflow: ''
    }
  },
  created() {
    this.loadContactWay()
  },
  computed: {
    whatsAppUrl() {
      return createWhatsAppUrl(this.contact.whatsapp)
    },
    privacyPolicyHref() {
      return '/privacy-policy'
    },
    contactValidationMessages() {
      return {
        nameRequired: this.t('contact.nameRequired'),
        emailRequired: this.t('contact.emailRequired'),
        emailInvalid: this.t('contact.emailInvalid'),
        whatsappRequired: this.t('contact.whatsappRequired'),
        whatsappShort: this.t('contact.whatsappShort'),
        whatsappInvalid: this.t('contact.whatsappInvalid'),
        messageRequired: this.t('contact.messageRequired'),
        privacyRequired: this.t('contact.privacyRequired')
      }
    }
  },
  watch: {
    currentLanguage() {
      this.wechatQrCodeVisible = false
      this.loadContactWay()
    },
    open(isOpen) {
      if (isOpen) {
        if (typeof window !== 'undefined') window.AnovatorMarketingTracking?.trackFormPage()
        this.lockBodyScroll()
        document.addEventListener('keydown', this.handleKeydown)
        this.$nextTick(() => this.$refs.closeButton?.focus())
      } else {
        this.unlockBodyScroll()
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.unlockBodyScroll()
  },
  methods: {
    async loadContactWay() {
      const request = this.contactRequestGuard.start(this.currentLanguage)
      try {
        const contact = await contactWayAPI.getCurrent(request.siteType)
        if (!this.contactRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.contact = {
          phone: typeof contact?.phone === 'string' ? contact.phone.trim() : '',
          email: typeof contact?.email === 'string' ? contact.email.trim() : '',
          whatsapp: typeof contact?.whatsapp === 'string' ? contact.whatsapp.trim() : '',
          wechatNo: typeof contact?.wechatNo === 'string' ? contact.wechatNo.trim() : '',
          wechatQrCodeUrl: typeof contact?.wechatQrCodeUrl === 'string' ? contact.wechatQrCodeUrl.trim() : ''
        }
      } catch (_) {
        if (!this.contactRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.contact = { phone: '', email: '', whatsapp: '', wechatNo: '', wechatQrCodeUrl: '' }
      }
    },
    clearError(field) {
      if (!this.errors[field]) return
      const nextErrors = { ...this.errors }
      delete nextErrors[field]
      this.errors = nextErrors
    },
    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return
      if (this.$refs.whatsappInput?.isCountryListOpen()) return
      if (this.wechatQrCodeVisible) this.wechatQrCodeVisible = false
      else this.requestClose()
    },
    lockBodyScroll() {
      this.previousBodyOverflow = document.body.style.overflow
      this.previousBodyPaddingRight = document.body.style.paddingRight
      this.previousRootOverflow = document.documentElement.style.overflow
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`
    },
    unlockBodyScroll() {
      document.body.style.overflow = this.previousBodyOverflow
      document.body.style.paddingRight = this.previousBodyPaddingRight
      document.documentElement.style.overflow = this.previousRootOverflow
    },
    requestClose() {
      this.$emit('close')
    },
    focusFirstError() {
      const firstField = ['name', 'email', 'whatsapp', 'message'].find((field) => this.errors[field])
      if (firstField) this.$refs[`${firstField}Input`]?.focus()
    },
    async submitForm(event) {
      event.preventDefault()
      if (this.isSubmitting) return

      this.errors = validateContactFields(this.fields, this.contactValidationMessages)
      this.submitStatus = { type: '', message: '' }
      if (Object.keys(this.errors).length) {
        this.$nextTick(this.focusFirstError)
        return
      }

      this.isSubmitting = true
      try {
        const whatsapp = await this.$refs.whatsappInput.getNumber()
        const request = createContactRequest({ ...this.fields, whatsapp })
        const response = await fetch(request.url, {
          method: request.method,
          headers: { 'Content-Type': request.contentType },
          body: request.body
        })
        await readContactResponse(response)
        if (typeof window !== 'undefined') window.AnovatorMarketingTracking?.trackSuccessfulSubmission()
        this.fields = createEmptyFields()
        this.phoneCountry = 'us'
        this.submitStatus = { type: 'success', message: 'Thanks for contacting us! We will get back to you soon.' }
      } catch (error) {
        this.submitStatus = { type: 'error', message: 'Unable to send your message right now. Please try again.' }
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.contact-modal-overlay,
.contact-modal-overlay * {
  box-sizing: border-box;
}

.contact-modal-overlay {
  position: fixed;
  top: var(--global-header-height, 44px);
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  display: flex;
  width: 100vw;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(215, 215, 215, 0.5);
  backdrop-filter: blur(10px);
}

.contact-modal {
  position: relative;
  display: grid;
  width: min(966px, calc(100vw - 48px));
  height: 500px;
  max-height: calc(100dvh - var(--global-header-height, 44px) - 48px);
  overflow: hidden;
  grid-template-columns: 385px 1fr;
  background: #fff;
  border-radius: 28px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.08);
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
}

.contact-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: block;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.contact-modal__close img {
  display: block;
  width: 24px;
  height: 24px;
}

.contact-modal__close:focus-visible,
.contact-modal__submit:focus-visible,
.contact-modal__privacy a:focus-visible,
.contact-modal__office-link:focus-visible,
.contact-modal__qr-close:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.contact-modal__details {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 80px 30px 80px 55px;
  background: #f5f5f7;
}

.contact-modal__details h2,
.contact-modal__details h3,
.contact-modal__details p {
  margin: 0;
}

.contact-modal__details h2 {
  color: #000;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 48px;
  line-height: 1.2;
}

.contact-modal__intro {
  width: 275px;
  margin-top: 10px !important;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.42;
}

.contact-modal__office h3 {
  margin-bottom: 10px;
  color: #000;
  font-family: 'MiSans-Bold', 'Segoe UI', sans-serif;
  font-size: 28px;
  line-height: 1.3;
}

.contact-modal__office p {
  display: flex;
  align-items: center;
  min-height: 25px;
  gap: 6px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 1.4;
}

.contact-modal__office p img {
  flex: 0 0 18px;
  object-fit: contain;
}

.contact-modal__office a {
  color: inherit;
  text-decoration: none;
}

.contact-modal__office-link {
  display: flex;
  align-items: center;
  min-height: 25px;
  padding: 0;
  gap: 6px;
  color: rgba(0, 0, 0, 0.6);
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 14px;
  line-height: 1.4;
  text-decoration: none;
}

.contact-modal__office-link[type='button'] {
  cursor: pointer;
}

.contact-modal__office-link img {
  flex: 0 0 18px;
  object-fit: contain;
}

.contact-modal__qr-overlay {
  position: absolute;
  z-index: 3;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.35);
}

.contact-modal__qr-dialog {
  position: relative;
  display: grid;
  width: min(280px, 100%);
  padding: 24px;
  place-items: center;
  background: #fff;
  border-radius: 16px;
}

.contact-modal__qr-dialog img {
  display: block;
  width: min(220px, 100%);
  aspect-ratio: 1;
  object-fit: contain;
}

.contact-modal__qr-close {
  position: absolute;
  top: 6px;
  right: 10px;
  padding: 0;
  color: #000;
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.contact-modal__form {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 90px 55px 50px 48px;
}

.contact-modal__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
  row-gap: 36px;
}

.contact-modal__field {
  position: relative;
  display: block;
  min-width: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
}

.contact-modal__field::before {
  position: absolute;
  top: 8px;
  left: 0;
  width: 5px;
  height: 5px;
  background: #000;
  border-radius: 50%;
  content: '';
}

.contact-modal__field--wide {
  grid-column: 1 / -1;
}

.contact-modal__field input,
.contact-modal__field textarea {
  display: block;
  width: 100%;
  height: 30px;
  margin: 0;
  padding: 0 8px 8px 12px;
  color: #000;
  background: transparent;
  border: 0;
  border-radius: 0;
  outline: none;
  font: 14px/1.4 'MiSans-Regular', 'Segoe UI', sans-serif;
  resize: none;
}

.contact-modal__field input::placeholder,
.contact-modal__field textarea::placeholder {
  color: rgba(0, 0, 0, 0.3);
  opacity: 1;
}

.contact-modal__field:focus-within {
  border-color: #ef0a6a;
}

.contact-modal__field.has-error {
  border-color: #cf174f;
}

.contact-modal__error {
  position: absolute;
  top: 32px;
  left: 0;
  color: #b0003a;
  font-size: 10px;
  line-height: 1.2;
  white-space: nowrap;
}

.contact-modal__privacy {
  display: flex;
  align-items: flex-start;
  margin-top: 30px;
  gap: 8px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 11px;
  line-height: 1.35;
}

.contact-modal__privacy input {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #ef0a6a;
}

.contact-modal__privacy a {
  color: #000;
  text-decoration: underline;
}

.contact-modal__privacy-error {
  margin: 4px 0 0 22px;
  color: #b0003a;
  font-size: 10px;
  line-height: 1.2;
}

.contact-modal__status {
  position: absolute;
  right: 55px;
  bottom: 104px;
  left: 48px;
  margin: 0;
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
}

.contact-modal__status.is-success {
  color: #17824b;
}

.contact-modal__status.is-error {
  color: #b0003a;
}

.contact-modal__submit {
  width: 100%;
  height: 50px;
  margin-top: auto;
  padding: 0 24px;
  color: #fff;
  background: #ef0a6a;
  border: 0;
  border-radius: 48px;
  font: 14px/50px 'MiSans-Regular', 'Segoe UI', sans-serif;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.contact-modal__submit:hover:not(:disabled) {
  background: #d6075d;
}

.contact-modal__submit:disabled {
  cursor: wait;
  opacity: 0.6;
}

@media (max-width: 700px) {
  .contact-modal-overlay {
    top: 50px;
    align-items: flex-start;
    padding: 16px;
    overflow: hidden;
  }

  .contact-modal {
    display: block;
    width: calc(100vw - 32px);
    height: auto;
    max-height: calc(100dvh - 82px);
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: 20px;
  }

  .contact-modal__close {
    position: sticky;
    top: 12px;
    float: right;
    margin: 12px 12px -36px 0;
  }

  .contact-modal__details {
    min-height: 310px;
    padding: 40px 24px 28px;
    gap: 36px;
  }

  .contact-modal__details h2 {
    font-size: clamp(34px, 10vw, 42px);
  }

  .contact-modal__intro {
    width: min(100%, 300px);
    font-size: 13px;
  }

  .contact-modal__office h3 {
    font-size: 24px;
  }

  .contact-modal__office p {
    font-size: 13px;
  }

  .contact-modal__form {
    min-height: 470px;
    padding: 42px 24px 28px;
  }

  .contact-modal__fields {
    grid-template-columns: 1fr;
    row-gap: 34px;
  }

  .contact-modal__field--wide {
    grid-column: auto;
  }

  .contact-modal__field input,
  .contact-modal__field textarea {
    font-size: 13px;
  }

  .contact-modal__privacy {
    margin-top: 28px;
  }

  .contact-modal__status {
    position: static;
    margin: 14px 0 0;
  }

  .contact-modal__submit {
    flex: 0 0 auto;
    height: 46px;
    margin-top: 28px;
    line-height: 46px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-modal__submit {
    transition: none;
  }
}
</style>
